import React, { useCallback, useMemo } from 'react';

import GoogleDriveIcon from '@atlaskit/legacy-custom-icons/google-drive-icon';
import MicrosoftSharepointIcon from '@atlaskit/legacy-custom-icons/microsoft-sharepoint-icon';
import SlackIcon from '@atlaskit/legacy-custom-icons/slack-icon-ai-mate';
import Link from '@atlaskit/link';
import { AtlassianIcon, ConfluenceIcon, JiraIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline, Text, xcss } from '@atlaskit/primitives';
import {
	type AssistanceServiceInputParam,
	type AgentKnowledgeSource as BaseAgentKnowledgeSource,
} from '@atlassian/conversation-assistant-service';

import { getAtlasHost } from '../../common/utils/atlas-url';
import { useKnowledgeConnectors } from '../knowledge-connector';

type AgentKnowledgeSource = BaseAgentKnowledgeSource & {
	configuration?: {
		space_filter?: string[];
		parent_filter?: string[];
		project_filter?: string[];
	};
};

type SourceNames = BaseAgentKnowledgeSource['source'];

const getIconForSource = (source: SourceNames) => {
	switch (source) {
		case 'confluence':
			return <ConfluenceIcon size="small" appearance="brand" />;
		case 'jira':
			return <JiraIcon size="small" appearance="brand" />;
		case 'atlas':
			return <AtlassianIcon size="small" appearance="brand" />;
		case 'microsoft_sharepoint':
			// TODO - Migrate to ADS icon/logo later - https://product-fabric.atlassian.net/browse/DSP-21174
			return <MicrosoftSharepointIcon size="medium" label="" />;
		case 'slack':
			// TODO - Migrate to ADS icon/logo later - https://product-fabric.atlassian.net/browse/DSP-21173
			return <SlackIcon size="medium" label="" />;
		case 'google_drive':
			// TODO - Migrate to ADS icon/logo later - https://product-fabric.atlassian.net/browse/DSP-21175
			return <GoogleDriveIcon size="medium" label="" />;
		default:
			return null;
	}
};

const getLabelForSource = (source: SourceNames) => {
	switch (source) {
		case 'confluence':
			return 'Confluence';
		case 'jira':
			return 'Jira';
		case 'atlas':
			return 'Atlassian Home';
		case 'microsoft_sharepoint':
			return 'Microsoft Sharepoint';
		case 'slack':
			return 'Slack';
		case 'google_drive':
			return 'Google Drive';
		default:
			return '';
	}
};

const wrapperStyles = xcss({
	height: '20px',
});

const KnowledgeSourceProduct = ({
	source,
	cloudUrl,
}: {
	source: SourceNames;
	cloudUrl?: string;
}) => {
	const LinkOrText = cloudUrl ? Link : Text;

	const getLabelRender = () => {
		switch (source) {
			case 'jira':
				return (
					<LinkOrText href={`${cloudUrl}/jira`} target="_blank">
						{getLabelForSource(source)}
					</LinkOrText>
				);
			case 'confluence':
				return (
					<LinkOrText href={`${cloudUrl}/wiki`} target="_blank">
						{getLabelForSource(source)}
					</LinkOrText>
				);
			case 'atlas':
				return (
					<Link href={getAtlasHost()} target="_blank">
						{getLabelForSource(source)}
					</Link>
				);
			default:
				return <Text>{getLabelForSource(source)}</Text>;
		}
	};

	return (
		<Inline xcss={wrapperStyles} alignBlock="center" space="space.075">
			{getIconForSource(source)}
			{getLabelRender()}
		</Inline>
	);
};

export const getKnowledgeProductsRender = ({
	sources,
	knowledgeScopeEnabled,
	allKnowledgeSourcesFiltered,
	cloudUrl,
}: {
	sources: Array<AgentKnowledgeSource>;
	knowledgeScopeEnabled: boolean;
	allKnowledgeSourcesFiltered: Array<AgentKnowledgeSource>;
	cloudUrl?: string;
}): React.ReactNode[] | null => {
	let processedSources = sources;

	// if knowledgeSource is not enabled, BE by default will use all the available knowledge sources, including any new ones that added
	// we want to simulate the BE behavior here only on rendering layer
	if (!knowledgeScopeEnabled && fg('rovo_enable_no_knowledge_sources_ui')) {
		processedSources = allKnowledgeSourcesFiltered;
	}

	const knowledgeSourcesRender: React.ReactNode[] = [];

	processedSources.forEach((source) => {
		if (!source.enabled) {
			return null;
		}

		// if confluence filters has any specific pages or space filter
		// we don't render the confluence product
		if (
			source.source === 'confluence' &&
			(source.configuration?.parent_filter?.length || source.configuration?.space_filter?.length)
		) {
			return null;
		}

		// if jira filters has any specific project filter
		// we don't render the jira product
		if (source.source === 'jira' && source.configuration?.project_filter?.length) {
			return null;
		}

		knowledgeSourcesRender.push(
			<KnowledgeSourceProduct key={source.source} source={source.source} cloudUrl={cloudUrl} />,
		);
	});

	return knowledgeSourcesRender.length ? knowledgeSourcesRender : null;
};

export const useKnowledgeProductsRender = ({
	assistanceServiceParams,
}: AssistanceServiceInputParam) => {
	const { enabledKnowledgeSources } = useKnowledgeConnectors({
		enabled: fg('rovo_enable_no_knowledge_sources_ui'),
		assistanceServiceParams,
	});

	const allKnowledgeSourcesFiltered = useMemo(
		() =>
			enabledKnowledgeSources.map(({ source }) => {
				const configuration =
					source === 'confluence'
						? { parent_filter: [], space_filter: [] }
						: source === 'jira'
							? { project_filter: [] }
							: undefined;
				return {
					source,
					configuration,
					enabled: true,
				} as AgentKnowledgeSource;
			}),
		[enabledKnowledgeSources],
	);

	const getKnowledgeProductsRenderForAgent = useCallback(
		(params: {
			sources: Array<AgentKnowledgeSource>;
			knowledgeScopeEnabled: boolean;
			cloudUrl?: string;
		}) => {
			return getKnowledgeProductsRender({
				...params,
				allKnowledgeSourcesFiltered,
			});
		},
		[allKnowledgeSourcesFiltered],
	);

	return {
		getKnowledgeProductsRenderForAgent,
	};
};
